<template>
	<div>
		<div class="h1-buttons">
			<h1>
				Test Runner
				<span
					v-if="isRunning"
					class="spinner-border spinner-border-sm mr-2"
					role="status"
					aria-hidden="true"
				></span>
			</h1>

			<div v-if="isNewTestRun && !loading && !error">
				<label class="mr-5">
					Stop Execution On Error
					<input
						type="checkbox"
						v-model="testRun.strategy"
						:true-value="EXECUTION_STRATEGY.FAIL_ON_FIRST"
						:false-value="EXECUTION_STRATEGY.EXECUTE_ALL"
					/>
				</label>
				<label >
					Run As
					<select v-model="testRun.userId">
						<option :value="null"></option>
						<option v-for="user in childrenUsers" :key="user.id" :value="user.id">{{user.username}}</option>
					</select>
				</label>
			</div>

			<div v-if="!loading">
				<button v-if="isRunning" class="btn btn-secondary mx-2 my-2" @click="cancelTestRun()">
					Stop
				</button>

				<button v-if="isNewTestRun && !error" class="btn btn-primary mx-2 my-2" @click="runTests()">
					Run
				</button>
				<button v-else class="btn btn-primary mx-2 my-2" @click="loadAllTests()">
					New Test Run
				</button>
			</div>
		</div>

		<ErrorAlert v-if="error" :error="error" />

		<Loading v-if="loading" :showText="true"/>
		<template v-else-if="hasRunId || isNewTestRun">
			<TestRunInfoPanel v-if="hasRunId" :testRun="testRun" @cancelTestRun="cancelTestRun()"/>
			<TestService v-for="service in testRun.servicesTests" @previewResult="previewResult" @cancelTest="cancelTest" :key="service.serviceId" :service="service" :mode="isNewTestRun ? 'select' : 'run'" />
		</template>
		
		<FullTextDialog ref="fullTextDialog" />
		<JsonViewDialog ref="jsonViewDialog" />

		<div v-if="showBackButton" class="d-flex justify-content-between align-items-center pb-5">
			<button class="btn btn-secondary" type="button" @click="onBack()">Back</button>
		</div>
	</div>
</template>

<script>
import FullTextDialog from './FullTextDialog';
import JsonViewDialog from './JsonViewDialog';
import TestService from './TestService';
import TestRunInfoPanel from './TestRunInfoPanel';
import ConsoleService from '../../services/console';
import { EXECUTION_STRATEGY, RUN_STATUS, SUCCESS_STATUS } from '../../util/test-runner-constants';
import { cancelTest, cancelTestRun, startPolling, stopPolling, fetchTestRun } from '../../util/test-runner';
import TestRunnerService from '../../services/test-runner';
import ErrorAlert from '../shared/ErrorAlert';
import Loading from '../shared/Loading';

export default {
	name: 'TestRunner',
	props: ["showBackButton"],
	components: {
		TestService,
		ErrorAlert,
		Loading,
		TestRunInfoPanel,
		FullTextDialog,
		JsonViewDialog
	},
	created() {
		this.EXECUTION_STRATEGY = EXECUTION_STRATEGY;
		this.RUN_STATUS = RUN_STATUS;
	},
	mounted() {
		ConsoleService.instance()
			.getUsers()
			.then((users) => this.childrenUsers = users);
		this.getLatestTestRun();
	},
	beforeDestroy() {
		stopPolling(this);
	},
	data: function() {
		return {
			childrenUsers: [],
			testRun: {
				runId: '',
				status: '',
				strategy: 'EXECUTE_ALL',
				userId: null,
				servicesTests: [],
			},
			loading: false,
			error: null,
			pollTimeoutId: null
		};
	},
	computed: {
		hasRunId() {
			return this.testRun && this.testRun.runId;
		},
		isNewTestRun(){
			return this.testRun && this.testRun.status === null;
		},
		isRunning() {
			return this.testRun && this.testRun.status === RUN_STATUS.RUNNING;
		}
	},
	methods: {
		getLatestTestRun() {
			this.loading = true;
			TestRunnerService.instance()
				.getLastRunning()
				.then((response) => {
					if (response.status == SUCCESS_STATUS) {
						if (response.data) {
							this.testRun = response.data;
							this.loading = false;
							this.error = null;
							startPolling(this.testRun.runId,this);
						} else {
							this.loadAllTests();
						}
					} else {
						this.error = "Could not fetch current test run, response status: " + response.status;
					}
				}).catch((err) => {
					this.loading = false;
					this.error = err;
				});
		},
		loadAllTests() {
			stopPolling(this);
			this.loading = true;
			TestRunnerService.instance()
				.listAllTests()
				.then((response) => {
					if (response.status == SUCCESS_STATUS && response.data) {
						this.testRun = response.data;
						this.testRun.strategy = EXECUTION_STRATEGY.EXECUTE_ALL;
						this.testRun.userId = null;
						this.error = null;
					} else {
						this.error = "Could not fetch the list of all tests, response status: " + response.status;
					}
					this.loading = false;
				}).catch((err) => {
					this.loading = false;
					this.error = err;
				});
		},
		runTests() {
			stopPolling(this);
			this.loading = true;
			TestRunnerService.instance()
				.runTests(this.testRun)
				.then((response) => {
					if (response.status == SUCCESS_STATUS && response.data) {
						this.error = null;
						fetchTestRun(response.data.runId, this);
					} else {
						this.error = "Could not run tests, response status: " + response.status;
					}
					this.loading = false;
				}).catch((err) => {
					this.loading = false;
					this.error = err;
				});
		},
		cancelTest(id) {
			cancelTest(id, this.testRun.runId, this);
		},
		cancelTestRun() {
			if (this.testRun && this.testRun.runId) {
				cancelTestRun(this.testRun.runId, this);
			}
		},
		openFullTextDialog(text) {
			this.$refs.fullTextDialog.show(text);
		},
		openJsonViewDialog(json) {
			this.$refs.jsonViewDialog.show(json);
		},
		previewResult(result) {
			if (typeof result === 'object') {
				this.openJsonViewDialog(result);
			} else {
				this.openFullTextDialog(result);
			}
		},
		onBack() {
			this.$emit("back");
		}
	}
};
</script>

<style>
	
</style>
